import { useState, useEffect, useCallback } from 'react'
import {
  getAdminOrders,
  getAdminOrder,
  simulateOrder,
  type AdminOrderSummary,
  type AdminOrderDetail,
  type AdminOrderFilters,
} from '../../services/adminApi'
import { usePermission } from '../../hooks/usePermission'
import { OrderDetailModal } from './OrderDetailModal'
import { CustomButton } from '../../components/common/CustomButton'
import { CustomTextInput } from '../../components/common/CustomTextInput'

const STATUS_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'paid', label: 'Paid' },
  { value: 'paid_held', label: 'Paid (Held)' },
  { value: 'completed', label: 'Completed' },
  { value: 'refund_pending', label: 'Refund Pending' },
  { value: 'refunded', label: 'Refunded' },
]

const FULFILLMENT_OPTIONS = [
  { value: '', label: 'All Fulfillment' },
  { value: 'pending', label: 'Pending' },
  { value: 'shipped', label: 'Shipped' },
  { value: 'ready_for_pickup', label: 'Ready for Pickup' },
  { value: 'picked_up', label: 'Picked Up' },
  { value: 'handed_off', label: 'Handed Off' },
]

const STATUS_COLORS: Record<string, string> = {
  paid: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  paid_held: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  completed: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  refund_pending: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  refunded: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  pending: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
}

const TRACKING_COLORS: Record<string, string> = {
  pre_transit: 'bg-gray-100 text-gray-600',
  in_transit: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',
  out_for_delivery: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400',
  delivered: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
}

function formatCents(cents: number | null | undefined) {
  if (cents == null) return '—'
  return `$${(cents / 100).toFixed(2)}`
}

function OrderRow({
  order,
  onClick,
}: {
  order: AdminOrderSummary
  onClick: () => void
}) {
  return (
    <tr
      tabIndex={0}
      className="hover:bg-gray-50 dark:hover:bg-[#1e1630] cursor-pointer transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#503864] dark:focus-visible:ring-[#b58fcc]"
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick()
        }
      }}
    >
      <td className="px-4 py-3">
        <div className="text-sm font-medium text-gray-900 dark:text-white truncate max-w-[180px]">
          {order.first_item_name ?? '—'}
        </div>
        <div className="text-xs text-gray-400 font-mono">{order.payment_ref?.slice(0, 8)}…</div>
      </td>
      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
        {formatCents(order.total_cents)}
      </td>
      <td className="px-4 py-3">
        <span
          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[order.status] ?? 'bg-gray-100 text-gray-600'}`}
        >
          {order.status}
        </span>
      </td>
      <td className="px-4 py-3">
        <span className="text-xs text-gray-600 dark:text-gray-400">
          {order.fulfillment_status ?? '—'}
        </span>
      </td>
      <td className="px-4 py-3">
        {order.tracking_status ? (
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${TRACKING_COLORS[order.tracking_status] ?? 'bg-gray-100 text-gray-600'}`}
          >
            {order.tracking_status.replace(/_/g, ' ')}
          </span>
        ) : (
          <span className="text-xs text-gray-400">—</span>
        )}
      </td>
      <td className="px-4 py-3 text-xs text-gray-400">
        {new Date(order.created_at).toLocaleDateString()}
      </td>
    </tr>
  )
}

// ─── Simulate Order Form ───────────────────────────────────────────────────────

interface SimulateFormProps {
  onCreated: (orderId: string) => void
  onCancel: () => void
}

function SimulateOrderForm({ onCreated, onCancel }: SimulateFormProps) {
  const [listingId, setListingId] = useState('')
  const [buyerUserId, setBuyerUserId] = useState('')
  const [quantity, setQuantity] = useState('1')
  const [buyerEmail, setBuyerEmail] = useState('simulated@test.barta')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!listingId.trim() || !buyerUserId.trim()) {
      setError('Listing ID and Buyer User ID are required')
      return
    }
    setLoading(true)
    setError(null)
    try {
      const res = await simulateOrder({
        listing_id: listingId.trim(),
        buyer_user_id: buyerUserId.trim(),
        quantity: parseInt(quantity, 10) || 1,
        buyer_email: buyerEmail.trim(),
      })
      if (res.ok && res.data) {
        onCreated(res.data.order_id)
      } else {
        setError(res.error?.message ?? 'Failed to create simulated order')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white dark:bg-[#1a1425] shadow-2xl p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
          Create Simulated Order
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
          Creates a synthetic paid order for a listing, bypassing Stripe.
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <CustomTextInput
            label="Listing ID (UUID)"
            value={listingId}
            onChange={(e) => setListingId(e.target.value)}
            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
            required
          />
          <CustomTextInput
            label="Buyer User ID (UUID)"
            value={buyerUserId}
            onChange={(e) => setBuyerUserId(e.target.value)}
            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
            required
          />
          <div className="grid grid-cols-2 gap-3">
            <CustomTextInput
              label="Quantity"
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              min="1"
            />
            <CustomTextInput
              label="Buyer Email"
              value={buyerEmail}
              onChange={(e) => setBuyerEmail(e.target.value)}
            />
          </div>
          {error && (
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          )}
          <div className="flex gap-3 pt-1">
            <CustomButton type="submit" disabled={loading} className="flex-1">
              {loading ? 'Creating…' : 'Create Order'}
            </CustomButton>
            <CustomButton type="button" variant="secondary" onClick={onCancel} className="flex-1">
              Cancel
            </CustomButton>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

export function Orders() {
  const [orders, setOrders] = useState<AdminOrderSummary[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [filters, setFilters] = useState<AdminOrderFilters>({ page: 1, page_size: 20 })
  const [selectedOrder, setSelectedOrder] = useState<AdminOrderDetail | null>(null)
  const [showSimulateForm, setShowSimulateForm] = useState(false)

  const canSimulate = usePermission('orders', 'simulate')

  const loadOrders = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await getAdminOrders(filters)
      if (res.ok && res.data) {
        setOrders(res.data.orders)
        setTotal(res.data.total)
        setPage(res.data.page)
        setTotalPages(res.data.total_pages)
      } else {
        setError(res.error?.message ?? 'Failed to load orders')
      }
    } catch {
      setError('Failed to connect to API')
    } finally {
      setLoading(false)
    }
  }, [filters])

  useEffect(() => {
    loadOrders()
  }, [loadOrders])

  const openOrder = async (id: string) => {
    const res = await getAdminOrder(id)
    if (res.ok && res.data) setSelectedOrder(res.data)
  }

  const handleOrderUpdated = (updated: AdminOrderDetail) => {
    setSelectedOrder(updated)
    setOrders((prev) => prev.map((o) => (o.id === updated.id ? { ...o, ...updated } : o)))
  }

  const handleSimulateCreated = async (orderId: string) => {
    setShowSimulateForm(false)
    await loadOrders()
    await openOrder(orderId)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Orders</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{total} total</p>
        </div>
        {canSimulate && (
          <CustomButton onClick={() => setShowSimulateForm(true)}>
            + Simulate Order
          </CustomButton>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <select
          value={filters.status ?? ''}
          onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value || undefined, page: 1 }))}
          className="rounded-lg border border-gray-200 dark:border-[#3d2c4f] bg-white dark:bg-[#1a1425] px-3 py-2 text-sm text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-[#7f4f9a]"
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <select
          value={filters.fulfillment_status ?? ''}
          onChange={(e) =>
            setFilters((f) => ({ ...f, fulfillment_status: e.target.value || undefined, page: 1 }))
          }
          className="rounded-lg border border-gray-200 dark:border-[#3d2c4f] bg-white dark:bg-[#1a1425] px-3 py-2 text-sm text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-[#7f4f9a]"
        >
          {FULFILLMENT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      {/* Table */}
      {error && (
        <div className="rounded-xl bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-600 dark:text-red-400">
          {error}
        </div>
      )}

      <div className="rounded-2xl bg-white dark:bg-[#1a1425] shadow-sm border border-gray-100 dark:border-[#2d2236] overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#7f4f9a] border-t-transparent" />
          </div>
        ) : orders.length === 0 ? (
          <div className="py-16 text-center text-gray-400">No orders found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-gray-100 dark:border-[#2d2236]">
                  {['Item', 'Total', 'Status', 'Fulfillment', 'Tracking', 'Date'].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-[#1e1630]">
                {orders.map((order) => (
                  <OrderRow key={order.id} order={order} onClick={() => openOrder(order.id)} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <CustomButton
            disabled={page <= 1}
            variant="secondary"
            onClick={() => setFilters((f) => ({ ...f, page: (f.page ?? 1) - 1 }))}
          >
            Previous
          </CustomButton>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            Page {page} of {totalPages}
          </span>
          <CustomButton
            disabled={page >= totalPages}
            variant="secondary"
            onClick={() => setFilters((f) => ({ ...f, page: (f.page ?? 1) + 1 }))}
          >
            Next
          </CustomButton>
        </div>
      )}

      {/* Modals */}
      {selectedOrder && (
        <OrderDetailModal
          order={selectedOrder}
          onClose={() => setSelectedOrder(null)}
          onUpdated={handleOrderUpdated}
        />
      )}
      {showSimulateForm && (
        <SimulateOrderForm
          onCreated={handleSimulateCreated}
          onCancel={() => setShowSimulateForm(false)}
        />
      )}
    </div>
  )
}
